import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { LayoutDashboard, Users, Settings, Mail, DollarSign, FileText, FileCheck, ChevronDown, LogOut } from 'lucide-react';
import { realDataService } from '@/shared/services/realDataService';
import { adminService } from '@/shared/services/realData/admin';
import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { UserProfile } from '@/shared/types';

// Import admin views
import AdminOverview from '@/features/admin/views/AdminOverview';
import AdminUsersView from '@/features/admin/views/AdminUsersView';
import UserDetailView from '@/features/admin/views/UserDetailView';
import AdminConfigView from '@/features/admin/views/AdminConfigView';
import AdminEmailView from '@/features/admin/views/AdminEmailView';
import AdminFinanceView from '@/features/admin/views/AdminFinanceView';
import AdminAuditView from '@/features/admin/views/AdminAuditView';
import AdminTemplatesView from '@/features/admin/views/AdminTemplatesView';

// Import admin components
import { AdminSidebar } from '@/features/admin/components/AdminSidebar';
import AdminLayoutSkeleton from '@/features/admin/components/AdminLayoutSkeleton';
import { UserActionDialog, ActionType } from '@/features/admin/components/users/UserActionDialog';
import { useAdminUsers } from '@/features/admin/hooks/useAdminUsers';

// Wrapper component for UserDetailView with action dialog support
const UserDetailViewWrapper: React.FC<{ onInitiateAction: (type: ActionType, user: UserProfile) => void }> = ({ onInitiateAction }) => {
  return <UserDetailView onInitiateAction={onInitiateAction} />;
};

interface AdminRoutesProps {
  onLogout: () => Promise<void>;
}

const AdminRoutes: React.FC<AdminRoutesProps> = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Global action dialog state for all user-related actions
  const [actionType, setActionType] = useState<ActionType>(null);
  const [actionUser, setActionUser] = useState<UserProfile | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [newPlanId, setNewPlanId] = useState<string>('basic');
  const [billingPeriod, setBillingPeriod] = useState<string>('monthly');

  // Handle initiating an action from any view
  const handleInitiateAction = (type: ActionType, targetUser: UserProfile) => {
    setActionType(type);
    setActionUser(targetUser);
    // Reset form state
    setTempPassword(null);
    setNewPlanId(targetUser.subscription.planId);
    setBillingPeriod(targetUser.subscription.billingPeriod);
  };

  // Handle confirming the action
  const handleConfirmAction = async () => {
    if (!actionUser || !actionType) return;

    try {
      switch (actionType) {
        case 'delete':
          await adminService.adminDeleteUser(actionUser.id);
          break;
        case 'reset_password':
          const password = await adminService.adminResetUserPassword(actionUser.id);
          setTempPassword(password);
          break;
        case 'change_plan':
          await adminService.adminUpdateUserPlan(actionUser.id, newPlanId, billingPeriod);
          break;
        case 'toggle_company':
          const newPlan = actionUser.subscription.planId === 'company' ? 'pro' : 'company';
          await adminService.adminUpdateUserPlan(actionUser.id, newPlan, 'yearly');
          break;
      }
      // Refresh data if needed
      setActionUser(null);
      setActionType(null);
    } catch (error) {
      console.error('Error executing action:', error);
      alert('Error executing action. Please try again.');
    }
  };

  // Handle canceling the action
  const handleCancelAction = () => {
    setActionType(null);
    setActionUser(null);
    setTempPassword(null);
  };

  const menuItems = [
    {
      path: '/admin/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
    },
    {
      path: '/admin/users',
      label: 'Users',
      icon: Users,
    },
    {
      path: '/admin/config',
      label: 'Settings',
      icon: Settings,
    },
    {
      path: '/admin/email',
      label: 'Email',
      icon: Mail,
    },
    {
      path: '/admin/finance',
      label: 'Finance',
      icon: DollarSign,
    },
    {
      path: '/admin/audit',
      label: 'Audit Logs',
      icon: FileText,
    },
    {
      path: '/admin/templates',
      label: 'Templates',
      icon: FileCheck,
    }
  ];

  const isActive = (path: string) => {
    if (path === '/admin/dashboard') {
      return location.pathname === '/admin' || location.pathname === '/admin/dashboard';
    }
    return location.pathname === path;
  };

  useEffect(() => {
    const loadUserAndTransactions = async () => {
      try {
        const currentUser = realDataService.getProfile();
        setUser(currentUser);

        // Load transactions for finance view
        try {
          const transactionsData = await realDataService.adminGetTransactions();
          setTransactions(transactionsData);
        } catch (txError) {
          console.warn('Could not load transactions:', txError);
          setTransactions([]);
        }
      } catch (error) {
        console.error('Error loading user:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserAndTransactions();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return <AdminLayoutSkeleton />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Admin Sidebar */}
      <AdminSidebar
        activePath={location.pathname}
        onNavigate={navigate}
        onLogout={onLogout}
        userProfile={user}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {menuItems.find(item => isActive(item.path))?.label || 'Dashboard'}
            </h1>
          </div>
          <div className="flex items-center gap-4" ref={dropdownRef}>
            {/* User Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center gap-3 px-3 py-2 rounded bg-white border border-gray-200 hover:border-gray-300 transition-all"
              >
                {/* Avatar */}
                <div className="w-8 h-8 bg-gray-900 rounded flex items-center justify-center">
                  <span className="text-white text-sm font-semibold">
                    {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'K'}
                  </span>
                </div>
                {/* User Info */}
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.name || 'Kennedy Mwangi'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.email || 'info@swaysuite.com'}
                  </p>
                </div>
                {/* Chevron */}
                <ChevronDown
                  size={14}
                  className={`text-gray-400 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown Menu */}
              {isProfileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.name || 'Kennedy Mwangi'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.email || 'info@swaysuite.com'}
                    </p>
                  </div>

                  <button
                    onClick={async () => {
                      await onLogout();
                      setIsProfileDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LogOut size={14} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content Area with Scroll */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            <Routes>
            {/* Admin Dashboard Routes - use relative paths for nested routes */}
            <Route path="dashboard" element={<AdminOverview />} />

            {/* User Management */}
            <Route path="users" element={<AdminUsersView />} />
            <Route path="users/:userId" element={<UserDetailViewWrapper onInitiateAction={handleInitiateAction} />} />

            {/* System Configuration */}
            <Route path="config" element={<AdminConfigView />} />

            {/* Email Templates */}
            <Route path="email" element={<AdminEmailView />} />

            {/* Finance/Billing */}
            <Route path="finance" element={<AdminFinanceView />} />

            {/* Audit Logs */}
            <Route path="audit" element={<AdminAuditView />} />

            {/* Templates */}
            <Route path="templates" element={<AdminTemplatesView />} />
          </Routes>

          {/* Global Action Dialog - rendered outside Routes but inside content area */}
          {location.pathname.startsWith('/admin/users') && (
            <UserActionDialog
              actionType={actionType}
              actionUser={actionUser}
              tempPassword={tempPassword}
              newPlanId={newPlanId}
              billingPeriod={billingPeriod}
              onNewPlanIdChange={setNewPlanId}
              onBillingPeriodChange={setBillingPeriod}
              onConfirm={handleConfirmAction}
              onCancel={handleCancelAction}
            />
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRoutes;